import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import FormInput from "../components/FormInput";
import PrimaryButton from "../components/PrimaryButton";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";

const normalizeRole = (role) =>
  String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const parseApiMessage = async (response, fallbackMessage) => {
  const responseText = await response.text();
  if (!responseText) {
    return fallbackMessage;
  }
  try {
    const payload = JSON.parse(responseText);
    return payload?.message || payload?.detail || fallbackMessage;
  } catch (error) {
    return responseText;
  }
};

const parseResponseAsJson = async (response) => {
  const responseText = await response.text();
  if (!responseText) {
    return null;
  }
  try {
    return JSON.parse(responseText);
  } catch (error) {
    return null;
  }
};

function ZilaManagementPage() {
  const navigate = useNavigate();
  const isSuperAdmin = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY)) === "SUPER_ADMIN";
  const [zilas, setZilas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [newZilaName, setNewZilaName] = useState("");
  const [newZilaElaqas, setNewZilaElaqas] = useState("");
  const [editingZilaId, setEditingZilaId] = useState("");
  const [editingZilaName, setEditingZilaName] = useState("");
  const [addingElaqaForZilaId, setAddingElaqaForZilaId] = useState("");
  const [newElaqaName, setNewElaqaName] = useState("");
  const [editingElaqaId, setEditingElaqaId] = useState("");
  const [editingElaqaName, setEditingElaqaName] = useState("");
  const [workingKey, setWorkingKey] = useState("");
  const [expandedZilaIds, setExpandedZilaIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const getZilaEditKey = (zila, index) => {
    const rawId = zila?.id;
    if (rawId !== null && rawId !== undefined && String(rawId).trim() !== "") {
      return `zila-${rawId}`;
    }
    return `zila-idx-${index}`;
  };

  const getElaqaEditKey = (zilaId, elaqa, index) => {
    const rawId = elaqa?.id;
    if (rawId !== null && rawId !== undefined && String(rawId).trim() !== "") {
      return `${zilaId}-${rawId}`;
    }
    return `${zilaId}-idx-${index}`;
  };

  const normalizedZilas = useMemo(
    () =>
      zilas.map((zila) => ({
        id: zila.id,
        name: zila.name || "",
        elaqas: Array.isArray(zila.elaqas) ? zila.elaqas : [],
      })),
    [zilas]
  );

  const filteredZilas = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) {
      return normalizedZilas;
    }
    return normalizedZilas.filter((zila) => {
      if (zila.name.toLowerCase().includes(query)) {
        return true;
      }
      return zila.elaqas.some((elaqa) => String(elaqa.name || "").toLowerCase().includes(query));
    });
  }, [normalizedZilas, searchTerm]);

  const fetchZilas = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/zilas`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to load zilas."));
      }

      const payload = await parseResponseAsJson(response);
      const zilaList = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      setZilas(zilaList);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load zilas.");
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!isSuperAdmin) {
      return;
    }
    fetchZilas();
  }, [fetchZilas, isSuperAdmin]);

  const resetInlineEditors = () => {
    setEditingZilaId("");
    setEditingZilaName("");
    setAddingElaqaForZilaId("");
    setNewElaqaName("");
    setEditingElaqaId("");
    setEditingElaqaName("");
  };

  const toggleZilaExpanded = (zilaId) => {
    setExpandedZilaIds((prev) =>
      prev.includes(zilaId) ? prev.filter((item) => item !== zilaId) : [...prev, zilaId]
    );
  };

  const handleCreateZila = async (event) => {
    event.preventDefault();
    const trimmedName = newZilaName.trim();
    if (!trimmedName) {
      setErrorMessage("Zila name is required.");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    const elaqas = newZilaElaqas
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    try {
      setIsSubmitting(true);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/zilas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: trimmedName,
          elaqas,
        }),
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to create zila."));
      }

      setMessage(`Zila "${trimmedName}" created successfully.`);
      setNewZilaName("");
      setNewZilaElaqas("");
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to create zila.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateZila = async (zilaId) => {
    const parsedZilaId = Number(zilaId);
    if (!Number.isInteger(parsedZilaId) || parsedZilaId <= 0) {
      setErrorMessage("Unable to update this zila because its ID is missing.");
      return;
    }

    const trimmedName = editingZilaName.trim();
    if (!trimmedName) {
      setErrorMessage("Zila name is required.");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setWorkingKey(`zila-update-${parsedZilaId}`);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/zilas/${parsedZilaId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedName }),
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to update zila."));
      }

      setMessage("Zila updated successfully.");
      setEditingZilaId("");
      setEditingZilaName("");
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update zila.");
    } finally {
      setWorkingKey("");
    }
  };

  const handleDeleteZila = async (zilaId, zilaName) => {
    const parsedZilaId = Number(zilaId);
    if (!Number.isInteger(parsedZilaId) || parsedZilaId <= 0) {
      setErrorMessage("Unable to delete this zila because its ID is missing.");
      return;
    }

    const shouldDelete = window.confirm(`Delete zila "${zilaName}" and its elaqas?`);
    if (!shouldDelete) {
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setWorkingKey(`zila-delete-${parsedZilaId}`);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/zilas/${parsedZilaId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to delete zila."));
      }

      setMessage(`Zila "${zilaName}" deleted successfully.`);
      resetInlineEditors();
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete zila.");
    } finally {
      setWorkingKey("");
    }
  };

  const handleAddElaqa = async (zilaId) => {
    const trimmedName = newElaqaName.trim();
    if (!trimmedName) {
      setErrorMessage("Elaqa name is required.");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setWorkingKey(`elaqa-add-${zilaId}`);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/zilas/${zilaId}/elaqas`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedName }),
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to add elaqa."));
      }

      setMessage("Elaqa added successfully.");
      setAddingElaqaForZilaId("");
      setNewElaqaName("");
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to add elaqa.");
    } finally {
      setWorkingKey("");
    }
  };

  const handleUpdateElaqa = async (elaqaId) => {
    const parsedElaqaId = Number(elaqaId);
    if (!Number.isInteger(parsedElaqaId) || parsedElaqaId <= 0) {
      setErrorMessage("Unable to update this elaqa because its ID is missing.");
      return;
    }

    const trimmedName = editingElaqaName.trim();
    if (!trimmedName) {
      setErrorMessage("Elaqa name is required.");
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setWorkingKey(`elaqa-update-${parsedElaqaId}`);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(
        `${getBaseApiUrl()}/super-admin/locations/elaqas/${parsedElaqaId}`,
        {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedName }),
      }
      );

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to update elaqa."));
      }

      setMessage("Elaqa updated successfully.");
      setEditingElaqaId("");
      setEditingElaqaName("");
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update elaqa.");
    } finally {
      setWorkingKey("");
    }
  };

  const handleDeleteElaqa = async (elaqaId, elaqaName) => {
    const parsedElaqaId = Number(elaqaId);
    if (!Number.isInteger(parsedElaqaId) || parsedElaqaId <= 0) {
      setErrorMessage("Unable to delete this elaqa because its ID is missing.");
      return;
    }

    const shouldDelete = window.confirm(`Delete elaqa "${elaqaName}"?`);
    if (!shouldDelete) {
      return;
    }

    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setWorkingKey(`elaqa-delete-${parsedElaqaId}`);
      setErrorMessage("");
      setMessage("");
      const response = await fetch(`${getBaseApiUrl()}/super-admin/locations/elaqas/${parsedElaqaId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(await parseApiMessage(response, "Unable to delete elaqa."));
      }

      setMessage(`Elaqa "${elaqaName}" deleted successfully.`);
      setEditingElaqaId("");
      setEditingElaqaName("");
      await fetchZilas();
    } catch (error) {
      setErrorMessage(error.message || "Unable to delete elaqa.");
    } finally {
      setWorkingKey("");
    }
  };

  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card">
      <h2>Zila Management</h2>
      <p className="dashboard-card-subtitle">
        Create, update, and delete zilas and their elaqas.
      </p>

      {message ? (
        <p className="dashboard-success-message" role="status">
          {message}
        </p>
      ) : null}
      {errorMessage ? (
        <p className="dashboard-error-message" role="alert">
          {errorMessage}
        </p>
      ) : null}

      <form onSubmit={handleCreateZila} noValidate>
        <FormInput
          id="new-zila-name"
          name="new-zila-name"
          label="Zila Name"
          value={newZilaName}
          onChange={(event) => setNewZilaName(event.target.value)}
          placeholder="Enter zila name"
          required
        />
        <FormInput
          id="new-zila-elaqas"
          name="new-zila-elaqas"
          label="Elaqas (optional, comma separated)"
          value={newZilaElaqas}
          onChange={(event) => setNewZilaElaqas(event.target.value)}
          placeholder="Model Town, Johar Town"
        />
        <PrimaryButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Creating..." : "Create Zila"}
        </PrimaryButton>
      </form>

      <section className="dashboard-users-list" aria-label="Zila list">
        <div className="dashboard-users-list-header">
          <h3>Zilas</h3>
          <div className="zila-toolbar-actions">
            <input
              className="score-filter-select"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search zila or elaqa..."
            />
            <PrimaryButton onClick={fetchZilas}>{isLoading ? "Refreshing..." : "Refresh"}</PrimaryButton>
          </div>
        </div>

        {isLoading ? <p>Loading zilas...</p> : null}
        {!isLoading && filteredZilas.length === 0 ? <p>No zila found.</p> : null}

        {!isLoading && filteredZilas.length > 0 ? (
          <div className="dashboard-users-table-wrapper">
            <table className="dashboard-users-table zila-management-table">
              <thead>
                <tr>
                  <th scope="col">Zila</th>
                  <th scope="col">Elaqas</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredZilas.map((zila, zilaIndex) => {
                  const isExpanded = expandedZilaIds.includes(zila.id);
                  const zilaEditKey = getZilaEditKey(zila, zilaIndex);
                  const hasValidZilaId = Number.isInteger(Number(zila?.id)) && Number(zila?.id) > 0;
                  const previewElaqas = zila.elaqas.slice(0, 4);
                  const remainingCount = Math.max(zila.elaqas.length - previewElaqas.length, 0);
                  return (
                    <Fragment key={`zila-${zila.id}`}>
                      <tr>
                        <td className="zila-name-cell">
                          <button
                            type="button"
                            className="zila-toggle-btn"
                            onClick={() => toggleZilaExpanded(zila.id)}
                            aria-expanded={isExpanded}
                            aria-label={`${isExpanded ? "Collapse" : "Expand"} ${zila.name}`}
                          >
                            {isExpanded ? "⌄" : "›"}
                          </button>
                          {editingZilaId === zilaEditKey ? (
                            <div className="zila-inline-editor">
                              <input
                                value={editingZilaName}
                                onChange={(event) => setEditingZilaName(event.target.value)}
                                className="score-filter-select"
                                placeholder="Enter zila name"
                              />
                              <div className="zila-inline-actions">
                                <button
                                  type="button"
                                  className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                  onClick={() => handleUpdateZila(zila.id)}
                                  disabled={!hasValidZilaId || workingKey === `zila-update-${zila.id}`}
                                >
                                  {workingKey === `zila-update-${zila.id}` ? "Saving..." : "Save"}
                                </button>
                                <button
                                  type="button"
                                  className="restriction-toggle-btn restriction-toggle-btn-restrict"
                                  onClick={() => {
                                    setEditingZilaId("");
                                    setEditingZilaName("");
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span className="zila-name-text">
                              {zila.name} <span className="zila-count-badge">{zila.elaqas.length}</span>
                            </span>
                          )}
                        </td>
                        <td className="zila-elaqa-cell">
                          {previewElaqas.length > 0 ? (
                            <div className="zila-chip-wrap">
                              {previewElaqas.map((elaqa) => (
                                <span key={`chip-${zila.id}-${elaqa.id}`} className="zila-elaqa-chip">
                                  {elaqa.name}
                                </span>
                              ))}
                              {remainingCount > 0 ? (
                                <button
                                  type="button"
                                  className="zila-more-chip"
                                  onClick={() => toggleZilaExpanded(zila.id)}
                                >
                                  +{remainingCount} more
                                </button>
                              ) : null}
                            </div>
                          ) : (
                            <p className="zila-empty-text">No elaqas</p>
                          )}
                        </td>
                        <td className="zila-action-cell">
                          {editingZilaId === zilaEditKey ? null : (
                            <div className="zila-inline-actions">
                              <button
                                type="button"
                                className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                onClick={() => {
                                  setEditingZilaId(zilaEditKey);
                                  setEditingZilaName(zila.name || "");
                                }}
                                disabled={!hasValidZilaId}
                                title={hasValidZilaId ? "Edit" : "Cannot edit because zila ID is missing"}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="restriction-toggle-btn restriction-toggle-btn-restrict"
                                onClick={() => handleDeleteZila(zila.id, zila.name || "")}
                                disabled={!hasValidZilaId || workingKey === `zila-delete-${zila.id}`}
                                title={
                                  hasValidZilaId ? "Delete" : "Cannot delete because zila ID is missing"
                                }
                              >
                                {workingKey === `zila-delete-${zila.id}` ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                      {isExpanded ? (
                        <tr className="zila-expanded-row">
                          <td colSpan={3}>
                            <div className="zila-expanded-content">
                              <div className="zila-expanded-header">
                                <p className="zila-expanded-title">All Elaqas</p>
                                <button
                                  type="button"
                                  className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                  onClick={() => {
                                    setAddingElaqaForZilaId(String(zila.id));
                                    setNewElaqaName("");
                                  }}
                                >
                                  Add Elaqa
                                </button>
                              </div>

                              {addingElaqaForZilaId === String(zila.id) ? (
                                <div className="zila-inline-editor">
                                  <input
                                    className="score-filter-select"
                                    value={newElaqaName}
                                    onChange={(event) => setNewElaqaName(event.target.value)}
                                    placeholder="Enter elaqa name"
                                  />
                                  <div className="zila-inline-actions">
                                    <button
                                      type="button"
                                      className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                      onClick={() => handleAddElaqa(zila.id)}
                                      disabled={workingKey === `elaqa-add-${zila.id}`}
                                    >
                                      {workingKey === `elaqa-add-${zila.id}` ? "Adding..." : "Save"}
                                    </button>
                                    <button
                                      type="button"
                                      className="restriction-toggle-btn restriction-toggle-btn-restrict"
                                      onClick={() => {
                                        setAddingElaqaForZilaId("");
                                        setNewElaqaName("");
                                      }}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              ) : null}

                              <div className="zila-expanded-elaqa-list">
                                {zila.elaqas.map((elaqa, index) => {
                                  const elaqaEditKey = getElaqaEditKey(zila.id, elaqa, index);
                                  const hasValidElaqaId =
                                    Number.isInteger(Number(elaqa?.id)) && Number(elaqa?.id) > 0;
                                  return (
                                  <div key={`expanded-${zila.id}-${elaqaEditKey}`} className="zila-expanded-elaqa-item">
                                    {editingElaqaId === elaqaEditKey ? (
                                      <input
                                        value={editingElaqaName}
                                        onChange={(event) => setEditingElaqaName(event.target.value)}
                                        className="score-filter-select"
                                      />
                                    ) : (
                                      <span>{elaqa.name}</span>
                                    )}

                                    {editingElaqaId === elaqaEditKey ? (
                                      <div className="zila-inline-actions">
                                        <button
                                          type="button"
                                          className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                          onClick={() => handleUpdateElaqa(elaqa.id)}
                                          disabled={workingKey === `elaqa-update-${elaqa.id}`}
                                        >
                                          {workingKey === `elaqa-update-${elaqa.id}` ? "Saving..." : "Save"}
                                        </button>
                                        <button
                                          type="button"
                                          className="restriction-toggle-btn restriction-toggle-btn-restrict"
                                          onClick={() => {
                                            setEditingElaqaId("");
                                            setEditingElaqaName("");
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="zila-inline-actions">
                                        <button
                                          type="button"
                                          className="restriction-toggle-btn restriction-toggle-btn-unrestrict"
                                          onClick={() => {
                                            setEditingElaqaId(elaqaEditKey);
                                            setEditingElaqaName(elaqa.name || "");
                                          }}
                                          disabled={!hasValidElaqaId}
                                          title={
                                            hasValidElaqaId
                                              ? "Edit"
                                              : "Cannot edit because elaqa ID is missing"
                                          }
                                        >
                                          Edit
                                        </button>
                                        <button
                                          type="button"
                                          className="restriction-toggle-btn restriction-toggle-btn-restrict"
                                          onClick={() => handleDeleteElaqa(elaqa.id, elaqa.name || "")}
                                          disabled={!hasValidElaqaId || workingKey === `elaqa-delete-${elaqa.id}`}
                                          title={
                                            hasValidElaqaId
                                              ? "Delete"
                                              : "Cannot delete because elaqa ID is missing"
                                          }
                                        >
                                          {workingKey === `elaqa-delete-${elaqa.id}` ? "Deleting..." : "Delete"}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )})}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </section>
  );
}

export default ZilaManagementPage;
